import { useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getRevenueSeries, getRevenueTimeframeOptions } from "../../lib/revenueSeries";
import { formatCompactNumber, formatPercent } from "../../lib/revenueFormatters";

function formatUsdTooltip(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function periodSummary(points) {
  if (!points?.length) return { total: null, growthPct: null };
  const total = points.reduce((s, p) => s + (Number(p.revenue) || 0), 0);
  if (points.length < 4) return { total, growthPct: null };
  const mid = Math.floor(points.length / 2);
  const first = points.slice(0, mid);
  const second = points.slice(mid);
  const a1 = first.reduce((s, p) => s + (Number(p.revenue) || 0), 0) / first.length;
  const a2 = second.reduce((s, p) => s + (Number(p.revenue) || 0), 0) / second.length;
  if (!a1) return { total, growthPct: null };
  const growthPct = ((a2 - a1) / a1) * 100;
  return { total, growthPct };
}

/**
 * @param {{
 *   chart?: { revenue_history?: unknown[]; revenue_snapshots?: unknown[]; anchors?: Record<string, unknown>; quality?: string };
 *   hasMarketData?: boolean;
 *   emptyCopy?: string | null;
 * }} props
 */
export default function RevenueChart({ chart, hasMarketData, emptyCopy }) {
  const options = getRevenueTimeframeOptions();
  const [tf, setTf] = useState("30d");

  const config = useMemo(
    () => ({
      revenueHistory: Array.isArray(chart?.revenue_history) ? chart.revenue_history : [],
      revenueSnapshots: Array.isArray(chart?.revenue_snapshots) ? chart.revenue_snapshots : [],
      anchors: chart?.anchors || {},
    }),
    [chart]
  );

  const { source, points } = useMemo(() => getRevenueSeries(config, tf), [config, tf]);
  const { total, growthPct } = useMemo(() => periodSummary(points), [points]);
  const hasMrrLine = points.some((p) => p.mrr != null && Number(p.mrr) > 0);

  const dataQualityLabel =
    source === "verified" ? "Verified revenue history" : "Modeled from verified peer anchors";

  if (!points.length) {
    if (!hasMarketData) {
      return (
        <div className="rounded-2xl bg-[#0a0a0b] p-6 text-center text-sm text-zinc-500 md:border md:border-[#1F1F1F] md:p-8">
          {emptyCopy?.trim() ||
            "Configure the revenue benchmark API on the server to load verified market data."}
        </div>
      );
    }
    return (
      <div className="rounded-2xl bg-[#0a0a0b] p-6 text-center text-sm text-zinc-500 md:border md:border-[#1F1F1F] md:p-8">
        Not enough reliable revenue anchors to draw a chart. Use the KPI cards and peer table above.
      </div>
    );
  }

  return (
    <div className="-mx-2 rounded-2xl bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.14),transparent_34%),linear-gradient(180deg,#101014,#09090b)] p-3 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] sm:mx-0 sm:p-6 md:border md:border-white/[0.08]">
      <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Revenue signal</p>
          <div className="flex flex-wrap items-baseline gap-3">
            <span
              className="text-xl font-bold tabular-nums text-white sm:text-3xl"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {total != null ? formatUsdTooltip(total) : "—"}
            </span>
            <span className="text-xs text-zinc-500">{tf === "all" ? "estimated lifetime window" : "window total"}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-zinc-400">
            {growthPct != null && Number.isFinite(growthPct) ? (
              <span className={growthPct >= 0 ? "text-emerald-400/90" : "text-rose-400/90"}>
                {formatPercent(growthPct)} vs first half
              </span>
            ) : null}
            <span className="text-zinc-500">{dataQualityLabel}</span>
          </div>
        </div>
        <div className="flex w-full rounded-full bg-black/25 p-1 sm:w-fit sm:border sm:border-white/[0.07]">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setTf(o.key)}
              className={`flex-1 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors sm:flex-none sm:px-3 ${
                tf === o.key
                  ? "bg-[#7C3AED] text-white shadow-[0_8px_22px_rgba(124,58,237,0.28)]"
                  : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 h-[320px] w-full sm:h-[340px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 14, right: 2, left: -10, bottom: 2 }}>
            <defs>
              <linearGradient id="revAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.42} />
                <stop offset="58%" stopColor="#7C3AED" stopOpacity={0.13} />
                <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#ffffff08" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: "#71717A", fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={18}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: "#71717A", fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={42}
              tickFormatter={(v) => `$${formatCompactNumber(v)}`}
            />
            {hasMrrLine ? (
              <YAxis
                yAxisId="mrr"
                hide
                domain={["dataMin", "dataMax"]}
              />
            ) : null}
            {hasMrrLine ? (
              <Line
                yAxisId="mrr"
                type="monotone"
                dataKey="mrr"
                stroke="#22d3ee"
                strokeWidth={1.8}
                strokeOpacity={0.85}
                dot={false}
                activeDot={{ r: 4, fill: "#67e8f9", strokeWidth: 0 }}
                connectNulls
              />
            ) : null}
            <Tooltip
              cursor={{ stroke: "rgba(167,139,250,0.28)", strokeWidth: 1 }}
              contentStyle={{
                background: "rgba(10,10,12,0.96)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: "14px",
                boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
              }}
              labelStyle={{ color: "#f4f4f5", fontWeight: 700 }}
              itemStyle={{ color: "#d4d4d8" }}
              formatter={(value, name) => {
                const label = name === "mrr" ? "MRR" : "Revenue";
                return [formatUsdTooltip(value), label];
              }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="#a78bfa"
              strokeWidth={2.6}
              fill="url(#revAreaFill)"
              dot={false}
              activeDot={{ r: 5, fill: "#c4b5fd", strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {hasMrrLine ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-2">
            <span className="h-0.5 w-6 rounded-full bg-gradient-to-r from-indigo-400 to-violet-500" />
            Revenue
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-0.5 w-6 rounded-full bg-cyan-300/80" />
            MRR
          </span>
        </div>
      ) : null}
    </div>
  );
}
